import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useNoIndex } from "@/hooks/use-no-index";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AdminLogin = () => {
  useNoIndex();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] flex items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm p-8">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <Lock className="h-5 w-5 text-[#00D2D3]" />
          <span className="font-outfit font-semibold text-lg text-white">
            Akuant Solutions Admin
          </span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/70 text-sm">
              E-mail
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@akuantsolutions.com.br"
              className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#00D2D3]"
              data-testid="admin-login-email-input"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/70 text-sm">
              Senha
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#00D2D3]"
              data-testid="admin-login-password-input"
              required
            />
          </div>

          {error && (
            <p className="text-sm text-red-400" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="bg-[#00D2D3] text-black hover:bg-[#00D2D3]/90 rounded-full h-11 font-manrope font-semibold mt-2 transition-all duration-300"
            data-testid="admin-login-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
